import usePersistentStore, { getGlobalState } from '@gui/store/persistent/persistentStore';
import { Panels } from '@gui/constants/Panels';
import { CameraMode } from '@visualizer/types';
import useViewerStore, { getViewer } from 'store/viewerStore';
import { PolygonDrawerManager } from '@rendering/DrawingModule/PolygonDrawerManager';
import SensorPointPicker from '@rendering/SensorPointPicker';

type KeyCode = KeyboardEvent['code'];

class CameraController {
    private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
    private keyupHandler: ((event: KeyboardEvent) => void) | null = null;
    private readonly activeKeys: Set<KeyCode> = new Set();
    private dblclickHandler: ((event: MouseEvent) => void) | null = null;
    private clickHandler: ((event: MouseEvent) => void) | null = null;
    private viewerUnsub: (() => void) | null = null;
    private readonly sensorPicker = new SensorPointPicker();

    private readonly maxSpeedMultiplier = 10;
    private readonly minSpeedMultiplier = 1;
    private readonly movementKeys: Set<KeyCode> = new Set([
        'KeyW',
        'KeyA',
        'KeyS',
        'KeyD',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
    ]);

    constructor() {
        this.init();
    }

    public init(): void {
        if (this.keydownHandler) return;

        this.keydownHandler = (event: KeyboardEvent) => {
            const code = event.code;

            // Always prevent default for arrow keys to ensure camera movement takes priority
            if (this.movementKeys.has(code) && code.startsWith('Arrow')) {
                event.preventDefault();
            }

            // Ignore all key input if a text input field is in focus:
            if (this.isTextInputInFocus(event)) {
                this.activeKeys.delete(code);
                return;
            }

            this.activeKeys.add(code);

            const { ambient } = getGlobalState();
            const setPanel = usePersistentStore.getState().setPanel;

            if (code === 'NumpadAdd' || code === 'Period') {
                const next = Math.min(ambient.cameraWasdSpeed + 1, this.maxSpeedMultiplier);
                setPanel({ cameraWasdSpeed: next }, Panels.AMBIENT);
            }
            if (code === 'NumpadSubtract' || code === 'Slash') {
                const next = Math.max(ambient.cameraWasdSpeed - 1, this.minSpeedMultiplier);
                setPanel({ cameraWasdSpeed: next }, Panels.AMBIENT);
            }

            if (this.movementKeys.has(code)) {
                const viewer = getViewer();
                viewer.setContinuousRenderReason?.('wasd', true);
                viewer.requestRender?.('controls');
            }
        };

        this.keyupHandler = (event: KeyboardEvent) => {
            const code = event.code;

            // Always prevent default for arrow keys to ensure camera movement takes priority
            if (this.movementKeys.has(code) && code.startsWith('Arrow')) {
                event.preventDefault();
            }

            // Ignore all key input if a text input field is in focus:
            if (this.isTextInputInFocus(event)) {
                this.activeKeys.delete(code);
                return;
            }

            this.activeKeys.delete(code);
            if (this.movementKeys.has(code)) {
                const hasMovementKey = [...this.activeKeys].some((key) => this.movementKeys.has(key));
                if (!hasMovementKey) {
                    getViewer().setContinuousRenderReason?.('wasd', false);
                }
            }
        };

        this.dblclickHandler = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            const { type } = getGlobalState().ambient.currentCamera;

            //Check if we should ignore this double click
            if (this.shouldIgnoreDblClick(target, type)) return;

            // Reset camera
            getViewer().cameraHelper.resetStandard(type);
        };

        // Click handler for Sensor Point Selection:
        this.clickHandler = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            // Selection with Ctrl + Left Click:
            if (event.button !== 0 || !(event.ctrlKey || event.metaKey)) return;
            const { type } = getGlobalState().ambient.currentCamera;
            if (this.shouldIgnoreDblClick(target, type)) return;
            this.sensorPicker.pickAndSelect(event, getViewer().canvas);
        };

        window.addEventListener('keydown', this.keydownHandler, true);
        window.addEventListener('keyup', this.keyupHandler, true);

        const currentCanvas = getViewer().canvas;
        this.bindDblClick(currentCanvas);
        this.bindClick(currentCanvas);
        this.viewerUnsub = useViewerStore.subscribe((state, prevState) => {
            if (state.viewer === prevState.viewer) return;
            const prevCanvas = prevState.viewer?.canvas;
            const nextCanvas = state.viewer?.canvas;
            this.removeDblClickListener(prevCanvas);
            this.removeClickListener(prevCanvas);
            this.bindDblClick(nextCanvas);
            this.bindClick(nextCanvas);
        });
    }

    public destroy(): void {
        if (this.keydownHandler) {
            window.removeEventListener('keydown', this.keydownHandler, true);
        }
        if (this.keyupHandler) {
            window.removeEventListener('keyup', this.keyupHandler, true);
        }
        this.removeDblClickListener(getViewer().canvas);
        this.removeClickListener(getViewer().canvas);
        this.viewerUnsub?.();
        this.keydownHandler = null;
        this.keyupHandler = null;
        this.dblclickHandler = null;
        this.clickHandler = null;
        this.viewerUnsub = null;
        this.activeKeys.clear();
    }

    public getActiveKeys(): Set<KeyCode> {
        return this.activeKeys;
    }

    // Detect a text input field in Focus:
    private isTextInputInFocus(event: KeyboardEvent): boolean {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return false;

        const tagName = target.tagName;
        const isFormField = tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
        return isFormField || target.isContentEditable || target.closest('[contenteditable="true"]') !== null;
    }

    private bindDblClick(canvas?: HTMLCanvasElement | null): void {
        if (!this.dblclickHandler || !canvas) return;
        canvas.addEventListener('dblclick', this.dblclickHandler);
    }

    private removeDblClickListener(canvas?: HTMLCanvasElement | null): void {
        if (canvas && this.dblclickHandler) {
            canvas.removeEventListener('dblclick', this.dblclickHandler);
        }
    }

    // Click handler lifecycle for Sensor Point Selection:
    private bindClick(canvas?: HTMLCanvasElement | null): void {
        if (!this.clickHandler || !canvas) return;
        canvas.addEventListener('click', this.clickHandler);
    }

    private removeClickListener(canvas?: HTMLCanvasElement | null): void {
        if (canvas && this.clickHandler) canvas.removeEventListener('click', this.clickHandler);
    }

    private shouldIgnoreDblClick(target: HTMLElement | null, type: CameraMode): boolean {
        return !this.isResettableCamera(type) || this.isDrawerBlocking() || this.isClickInsideDrawer(target) || this.isGeoEditEnabled();
    }

    // Check if the camera type supports resetting
    private isResettableCamera(type: CameraMode): boolean {
        return type === CameraMode.STANDARD || type === CameraMode.TOP || type === CameraMode.ORTHO;
    }

    // Check if a drawing drawer is currently active and blocking camera reset
    private isDrawerBlocking(): boolean {
        const drawer = PolygonDrawerManager.getInstance().getActiveDrawer();
        return Boolean(drawer !== null && (drawer.editing || drawer.running));
    }

    // Check if the click target is inside any drawer UI elements
    private isClickInsideDrawer(target: HTMLElement | null): boolean {
        return Boolean(target?.closest('.gui-drawer, .drawer-handle, .gui-drawer-paper'));
    }

    // Check if geodata editing is currently enabled
    private isGeoEditEnabled(): boolean {
        return Boolean(getGlobalState().geodata?.editGeodataEnabled);
    }
}

export default CameraController;
